import { api } from '../api';
/**
 * The LIFF id comes from the server, not from this bundle.
 *
 * A LIFF app registers ONE endpoint URL in the LINE console. Hardcoding the live site's id here meant that
 * every deploy — staging included — handed people a link that resolves to dinkropenplay.com, so the login
 * button on staging silently took you to production. An environment with no LIFF app of its own gets null
 * and simply does not offer that button.
 */
let liffId: string | null | undefined;
let configPromise: Promise<string | null> | null = null;

export async function getLiffId(): Promise<string | null> {
  if (liffId !== undefined) return liffId;
  configPromise ??= fetch('/api/auth/client-config', { credentials: 'same-origin' })
    .then((r) => (r.ok ? (r.json() as Promise<{ liff_id?: string | null }>) : null))
    .then((j) => (liffId = j?.liff_id ?? null))
    .catch(() => (liffId = null));
  return configPromise;
}

export const liffLoginUrl = (id: string, returnTo: string) =>
  `https://liff.line.me/${id}/?dinkr_login=1&return_to=${encodeURIComponent(returnTo)}`;
const LOGGED_OUT = 'dinkr-liff-logged-out';
export function pauseLiffLogin() { try { sessionStorage.setItem(LOGGED_OUT, '1'); } catch {} }
const safePath = (path: string | null) => path?.startsWith('/') && !path.startsWith('//') && !path.includes('\\') ? path : '/';

/** Only LIFF/LINE launches load the SDK. Ordinary web browsing stays anonymous. */
export async function loginFromLiff(): Promise<string | null> {
  const query = new URLSearchParams(location.search);
  if (!/\bLine\//i.test(navigator.userAgent) && !query.has('liff.state') && !query.has('liffClientId') && !query.has('dinkr_login')) return null;
  // No LIFF app for this origin (staging, a preview): nothing to initialise, and the caller falls back
  // to the ordinary browser sign-in.
  const id = await getLiffId();
  if (!id) return null;
  const { default: liff } = await import('@line/liff');
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([liff.init({ liffId: id }), new Promise<never>((_, reject) => { timer = setTimeout(() => reject(new Error('เชื่อมต่อ LINE ไม่สำเร็จ กรุณาปิดแล้วเปิดลิงก์ใหม่')), 15000); })]);
  } finally { clearTimeout(timer); }
  const current = new URLSearchParams(location.search);
  const explicit = current.get('dinkr_login') === '1';
  try { if (explicit) sessionStorage.removeItem(LOGGED_OUT); else if (sessionStorage.getItem(LOGGED_OUT)) return null; } catch {}
  if (!liff.isInClient() && !liff.isLoggedIn()) {
    if (explicit) {
      const redirect = new URL(location.pathname, location.origin);
      redirect.searchParams.set('dinkr_login', '1');
      redirect.searchParams.set('return_to', safePath(current.get('return_to')));
      liff.login({ redirectUri: redirect.toString() });
    }
    return null;
  }
  const token = liff.getIDToken();
  if (!token) throw new Error('LINE ไม่ส่งข้อมูลเข้าสู่ระบบ กรุณาตรวจสิทธิ์ openid แล้วเปิดลิงก์ใหม่');
  await api<{ onboarded: boolean }>('/api/auth/liff', { method: 'POST', body: { id_token: token }, silent401: true });
  const returnTo = safePath(current.get('return_to'));
  // Remove launch parameters without storing the LINE token in browser storage.
  for (const key of ['dinkr_login', 'return_to']) current.delete(key);
  history.replaceState(history.state, '', location.pathname + (current.size ? `?${current}` : '') + location.hash);
  return explicit ? returnTo : null;
}
